// ============================================================
// TaskFlow OS - Design Node (React Flow Custom Node)
// ============================================================

import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeStatus } from '../../lib/types';
import { NODE_STATUS_LABELS, NODE_STATUS_COLORS, NODE_STATUS_ANIMATIONS } from '../../lib/constants';
import { IconDesign, IconUser } from '../../icons/SvgIcons';

interface DesignNodeProps {
  data: {
    label: string;
    status: NodeStatus;
    designType: 'design' | 'review' | 'prototype' | 'research' | 'testing';
    assigneeName?: string;
    assigneeAvatar?: string;
    version?: number;
    onEdit?: () => void;
  };
  selected: boolean;
}

const DESIGN_TYPE_LABELS: Record<string, string> = {
  design: 'デザイン',
  review: 'レビュー',
  prototype: 'プロトタイプ',
  research: 'リサーチ',
  testing: 'テスト',
};

const DesignNodeComponent: React.FC<DesignNodeProps> = ({ data, selected }) => {
  const statusColor = NODE_STATUS_COLORS[data.status];
  const animation = NODE_STATUS_ANIMATIONS[data.status];

  return (
    <div
      className={`
        relative min-w-[240px] max-w-[300px] rounded-2xl overflow-hidden cursor-pointer
        transition-all duration-500 ease-out ${animation}
        ${selected ? 'ring-2 ring-purple-400/40 scale-[1.02]' : ''}
      `}
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(24px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.8)',
        border: `1px solid ${selected ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
        boxShadow: `
          inset 0 1px 0 0 rgba(255, 255, 255, 0.12),
          inset 0 -2px 8px 0 rgba(0, 0, 0, 0.2),
          0 8px 32px -4px rgba(0, 0, 0, 0.25),
          0 0 12px -2px rgba(139, 92, 246, 0.15)
        `,
      }}
      onDoubleClick={data.onEdit}
    >
      {/* Refraction */}
      <div className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none rounded-t-2xl"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)' }} />

      {/* Gradient bar */}
      <div className="h-1 w-full"
        style={{
          background: `linear-gradient(90deg, #8b5cf6, #06b6d4)`,
          boxShadow: '0 0 8px rgba(139, 92, 246, 0.3)',
        }} />

      <div className="p-4 relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-purple-500/15 border border-purple-500/30">
            <IconDesign size={16} color="#8b5cf6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white truncate">{data.label}</h3>
            <span className="text-[10px] text-white/40">{DESIGN_TYPE_LABELS[data.designType] || data.designType}</span>
          </div>
          {data.version && data.version > 1 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-white/40 border border-white/10">
              v{data.version}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30` }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
            {NODE_STATUS_LABELS[data.status]}
          </span>

          {data.assigneeName && (
            <div className="flex items-center gap-1">
              <div className="w-5 h-5 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                <IconUser size={10} color="#8b5cf6" />
              </div>
              <span className="text-[10px] text-white/50">{data.assigneeName}</span>
            </div>
          )}
        </div>
      </div>

      <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-purple-500/50 !border-2 !border-purple-500/80" />
      <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-cyan-500/50 !border-2 !border-cyan-500/80" />
    </div>
  );
};

export default memo(DesignNodeComponent);
